import React, { useEffect, useRef, useState } from 'react';
import { Box, Grow } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { emailIsValidRe, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsFormGridItem from 'ui-elements/form-elements/FsFormGridItem';
import FsFormGridWrapper from 'ui-elements/form-elements/FsFormGridWrapper';
import FsPhoneNumberInput from 'ui-elements/form-elements/FsPhoneNumberInput';
import { Caption } from './PersonForm.style-elements';

interface CompanyFormProps {
  inStatus: boolean;
  transitionTime: number;
  onClose?: () => void;
}

const PersonForm: React.FC<CompanyFormProps> = ({
  inStatus,
  transitionTime,
  onClose,
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const fields = [
    {
      required: true,
      name: defaultLocales.firstName.toLowerCase(),
      value: firstName,
      label: defaultLocales.firstName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFirstName(e.target?.value);
      },
    },
    {
      name: defaultLocales.lastName.toLowerCase(),
      value: lastName,
      label: defaultLocales.lastName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setLastName(e.target?.value);
      },
    },
    {
      required: true,
      name: defaultLocales.email.toLowerCase(),
      value: email,
      label: defaultLocales.email,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target?.value);
      },
    },
    {
      required: true,
      name: 'phone',
      value: phone,
      label: defaultLocales.phoneNumber,
      placeholder: '+44*',
      country: 'gb',
      onChange: (phoneNumber: string): void => {
        setPhone(phoneNumber);
      },
    },
    {
      required: true,
      name: defaultLocales.reference.toLowerCase(),
      value: reference,
      label: defaultLocales.reference,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setReference(e.target?.value);
      },
    },
  ];

  const [firstNameField, lastNameField, ...otherFields] = fields;

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('44');
    setReference('');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      firstName,
      lastName,
      email,
      phone,
      reference,
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    enqueueSnackbar(defaultLocales.invitationSendNotificationSuccess, {
      variant: 'success',
    });
    resetForm();
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isPhoneValid = phone.length >= 5;
    const isEmailValid = !!email.match(emailIsValidRe);
    const isFirstNameValid = firstName.length >= 3;
    const isReferenceValid = reference.length >= 3;
    const isValid =
      isDefaultFormValid &&
      isPhoneValid &&
      isEmailValid &&
      isFirstNameValid &&
      isReferenceValid;
    setIsFormValid(isValid);
  }, [firstName, lastName, email, phone, reference]);

  return (
    <Grow in={inStatus} timeout={transitionTime}>
      <Box>
        <Caption>Customer Data</Caption>
        <form onSubmit={handleSubmit} ref={formEl}>
          <FsFormGridWrapper>
            {[firstNameField, lastNameField].map(
              ({ name, onChange, ...otherProps }) => (
                <FsFormGridItem key={name}>
                  <FsSimpleInput
                    {...{ ...otherProps, name }}
                    onChange={
                      onChange as (
                        e: React.ChangeEvent<HTMLInputElement>
                      ) => void
                    }
                    fsVariant="outlined"
                    fullWidth
                  />
                </FsFormGridItem>
              )
            )}
          </FsFormGridWrapper>
          {otherFields.map(({ name, onChange, ...otherProps }) => (
            <FsFormFullWidthContainer key={name}>
              {name === 'phone' ? (
                <FsPhoneNumberInput
                  {...otherProps}
                  onChange={onChange as (phoneNumber: string) => void}
                />
              ) : (
                <FsSimpleInput
                  {...{ ...otherProps, name }}
                  onChange={
                    onChange as (e: React.ChangeEvent<HTMLInputElement>) => void
                  }
                  fsVariant="outlined"
                  fullWidth
                />
              )}
            </FsFormFullWidthContainer>
          ))}
          <FsPendingButton
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            width={160}
            disabled={!isFormValid}
          >
            {defaultLocales.invite}
          </FsPendingButton>
        </form>
      </Box>
    </Grow>
  );
};

export default PersonForm;
